import { z } from 'zod';
import { FINANCIAL_PRODUCT, LABELS } from '../constants';
import { requiredFieldError, validateDateOfBirth, validateDriversLicenseCardNumber } from '../helper';
import { applicantWithIdentification, document, generateSchema } from '../shared';
import {
  baseLicense,
  expiryBNPL,
  identification,
  medicare,
  medicareExpiry,
  passport,
} from '../shared/model/identification';

const { DATE_OF_BIRTH } = LABELS;

const applicant = applicantWithIdentification.merge(
  z.object({
    dateOfBirth: z
      .date({
        required_error: requiredFieldError(DATE_OF_BIRTH),
        invalid_type_error: requiredFieldError(DATE_OF_BIRTH),
      })
      .refine(value => {
        return validateDateOfBirth(value);
      }, 'Enter a valid date of birth'),
    type: z.string().optional(),
  }),
);

const defaultSchema = {
  applicant: applicant,
  isSameAddress: z.boolean().optional(),
};

const actSlipSchema = {
  ...defaultSchema,
  isSkipDocuments: z.boolean().optional(),
  documents: document.default([]),
};

const electrify2515Schema = {
  applicant: applicant.merge(
    z.object({
      isConcessionCardHolder: z.boolean().default(false),
    }),
  ),
  isSameAddress: z.boolean().optional(),
  isSkipDocuments: z.boolean().optional(),
  documents: document.default([]),
};

const brightePaySchema = {
  isSameAddress: z.boolean().optional(),
  applicant: defaultSchema.applicant
    .merge(
      z.object({
        dateOfBirth: z
          .date({
            required_error: requiredFieldError(LABELS.DATE_OF_BIRTH),
            invalid_type_error: requiredFieldError(LABELS.DATE_OF_BIRTH),
          })
          .refine(value => {
            const eighteenYearsAgo = new Date();
            eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
            return value <= eighteenYearsAgo; // Must be 18 or older
          }, 'You must be at least 18 years old.')
          .refine(value => {
            const hundredYearsAgo = new Date();
            hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
            return value >= hundredYearsAgo; // Must be less than 100
          }, 'You must be less than 100 years old.'),
      }),
    )
    .extend({
      identification: identification.extend({
        drivingLicense: baseLicense
          .extend({
            expiryDate: expiryBNPL,
          })
          .superRefine((data, ctx) => {
            const { state, cardNumber, number } = data;
            const { isValid } = validateDriversLicenseCardNumber(state, cardNumber, number);
            if (!isValid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Incorrect driver's license card number",
                path: ['cardNumber'],
              });
            }
          })
          .optional(),
        passport: passport
          .extend({
            expiryDate: expiryBNPL,
          })
          .optional(),
        medicare: medicare
          .extend({
            expiryDate: medicareExpiry,
          })
          .optional(),
      }),
    }),
};

const customSchemas = {
  [FINANCIAL_PRODUCT.ACT_SLIP]: actSlipSchema,
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: electrify2515Schema,
  [FINANCIAL_PRODUCT.BRIGHTE_PAY]: brightePaySchema,
};

export const identificationFormSchema = generateSchema<typeof customSchemas, typeof defaultSchema>({
  customSchemas,
  defaultSchema,
}).superRefine((val, ctx) => {
  /**
   * Documents are required
   *  1. If the applicant is not a concession card holder
   *  2. If the admin has not skipped the document upload
   */
  if ('documents' in val && val.documents && !val.isSkipDocuments && val.applicant?.isConcessionCardHolder !== false) {
    if (val?.documents?.length == 0) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredFieldError(),
        path: ['documents'],
      });
    }
  }
});

export type IdentityType = z.infer<typeof identificationFormSchema> & {
  documents?: Array<{ documentId: string; fileName: string }>;
};
